"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import type { EditorType } from "../../../types";
import { Icons } from "../Icons";
import { cn } from "../../../utils/cn";
import {
  FONT_SIZE_MAX,
  FONT_SIZE_MIN,
  FONT_SIZE_PRESETS,
  parseFontSizePx,
  readSelectionFontSize,
  toFontSizeValue,
} from "../../../styles/FontSizeStyle";

interface FontSizeButtonProps {
  editor: EditorType | any;
}

const DEFAULT_LABEL = "기본";

/** "18px" → "18" */
const toLabel = (size: string) => size.replace(/px$/, "");

/**
 * 글자 크기 선택 드롭다운 (FloatingMenu용).
 *
 * 드롭다운 상단에 −/+ 스테퍼 + 직접 입력(1px 단위, 8~96px clamp)을 두고,
 * 그 아래 "기본" 리셋 + 프리셋 목록을 유지한다.
 *
 * ColorButton과 달리 테이블 셀 분기가 없다 — 셀 단위 font-size attr이 없으므로
 * 셀 내부에서도 항상 인라인 스타일(addStyles/removeStyles)로 적용한다.
 */
export const FontSizeButton: React.FC<FontSizeButtonProps> = ({ editor }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 현재 인라인 글자 크기 (선택 변경 시 FloatingMenu가 tick으로 리렌더).
  // 다중 블록 선택에서 getActiveStyles($to.marks)가 빈 값을 돌려주는 문제 때문에
  // 선택 범위 전체를 스캔하는 readSelectionFontSize를 사용한다.
  const live = readSelectionFontSize(editor);

  // 낙관적 갱신값: +/−·입력 적용 직후, tick 리렌더가 빈 값을 읽어도 누적되도록
  // 직전 적용값을 우선한다. live(실측)가 바뀌면(선택 이동 등) 해제한다.
  const [optimistic, setOptimistic] = useState<string | null>(null);
  const lastLiveRef = useRef<string>(live);
  useEffect(() => {
    if (live !== lastLiveRef.current) {
      lastLiveRef.current = live;
      setOptimistic(null);
    }
  }, [live]);

  const currentSize = optimistic ?? live;
  // 명시 크기가 없으면("기본") 14px를 기준으로 증감/표시한다.
  const currentPx = parseFontSizePx(currentSize);

  // 직접 입력 필드의 로컬 상태 (실제 크기 변경 시 동기화)
  const [inputValue, setInputValue] = useState<string>(String(currentPx));
  useEffect(() => {
    setInputValue(String(currentPx));
  }, [currentPx]);

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSizeSelect = useCallback(
    (size: string) => {
      try {
        if (!editor) return;
        if (size === "") {
          (editor as any).removeStyles?.({ fontSize: "" });
          setOptimistic(null);
        } else {
          (editor as any).addStyles?.({ fontSize: size });
          setOptimistic(size);
        }
        setIsOpen(false);
        setTimeout(() => editor.focus?.());
      } catch (err) {
        console.error("Font size apply failed:", err);
      }
    },
    [editor]
  );

  // −/+ 스테퍼: 메뉴를 닫지 않고 1px씩 적용 (선택 영역 유지).
  const stepBy = useCallback(
    (delta: number) => {
      try {
        const value = toFontSizeValue(currentPx + delta);
        (editor as any)?.addStyles?.({ fontSize: value });
        setOptimistic(value);
      } catch (err) {
        console.error("Font size step failed:", err);
      }
    },
    [editor, currentPx]
  );

  // 직접 입력 적용: 유효하면 clamp 후 적용, 무효/빈값이면 표시값 복원.
  const applyInput = useCallback(() => {
    const n = parseInt(inputValue, 10);
    if (Number.isFinite(n)) {
      try {
        const value = toFontSizeValue(n);
        (editor as any)?.addStyles?.({ fontSize: value });
        setOptimistic(value);
      } catch (err) {
        console.error("Font size apply failed:", err);
      }
    } else {
      setInputValue(String(currentPx));
    }
  }, [editor, inputValue, currentPx]);

  // 버튼 클릭 시 에디터 포커스/선택 영역 유지
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  return (
    <div className="lumir-dropdown-wrapper" ref={dropdownRef}>
      <button
        className="lumir-dropdown-btn lumir-font-size-btn"
        onClick={() => setIsOpen(!isOpen)}
        onMouseDown={handleMouseDown}
        title="글자 크기"
        type="button"
      >
        <span className="lumir-font-size-label">
          {currentSize ? toLabel(currentSize) : DEFAULT_LABEL}
        </span>
        {Icons.expandMore}
      </button>
      {isOpen && (
        <div className="lumir-dropdown-menu lumir-font-size-menu">
          {/* 1px 스테퍼 + 직접 입력 */}
          <div className="lumir-fs-stepper">
            <button
              type="button"
              className="lumir-fs-stepper-btn"
              aria-label="글자 크기 1px 줄이기"
              disabled={currentPx <= FONT_SIZE_MIN}
              onMouseDown={handleMouseDown}
              onClick={() => stepBy(-1)}
            >
              −
            </button>
            <input
              className="lumir-fs-stepper-input"
              type="text"
              inputMode="numeric"
              aria-label="글자 크기 입력"
              value={inputValue}
              onChange={(e) =>
                setInputValue(e.target.value.replace(/[^0-9]/g, ""))
              }
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyInput();
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  stepBy(1);
                } else if (e.key === "ArrowDown") {
                  e.preventDefault();
                  stepBy(-1);
                }
              }}
              onBlur={applyInput}
            />
            <button
              type="button"
              className="lumir-fs-stepper-btn"
              aria-label="글자 크기 1px 늘리기"
              disabled={currentPx >= FONT_SIZE_MAX}
              onMouseDown={handleMouseDown}
              onClick={() => stepBy(1)}
            >
              +
            </button>
          </div>
          <button
            className={cn(
              "lumir-dropdown-item",
              currentSize === "" && "is-active"
            )}
            onClick={() => handleSizeSelect("")}
            onMouseDown={handleMouseDown}
            type="button"
          >
            {DEFAULT_LABEL}
          </button>
          {FONT_SIZE_PRESETS.map((size) => (
            <button
              key={size}
              className={cn(
                "lumir-dropdown-item",
                currentSize === size && "is-active"
              )}
              onClick={() => handleSizeSelect(size)}
              onMouseDown={handleMouseDown}
              type="button"
            >
              {toLabel(size)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
